import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery, useQueryClient } from "react-query";
import UserService from "../services/userService";

interface AuthContextProps {
  isAuthenticated: boolean;
  showLoginModal: boolean;
  setShowLoginModal: Dispatch<SetStateAction<boolean>>;
  login: (token: string) => void;
  logout: () => void;
  authInfo: AuthInfoResponse | null;
  isAdminRole: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const token = useMemo(() => localStorage.getItem("token"), []);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const queryClient = useQueryClient();
  const { data: authInfo } = useQuery(["users", "auth-info"], async () => {
    if (!localStorage.getItem("token")) return null;
    try {
      return UserService.getAuthInfo();
    } catch {
      return null;
    }
  });
  useEffect(() => {
    setIsAuthenticated(!!authInfo?.id);
  }, [token, authInfo]);

  const login = (token: string) => {
    localStorage.setItem("token", token);
    setIsAuthenticated(true);
    setShowLoginModal(false);
    queryClient.invalidateQueries(["users", "auth-info"]);
  };

  const logout = () => {
    console.log("logout");
    localStorage.removeItem("token");
    localStorage.removeItem("cartItems");
    setIsAuthenticated(false);
    queryClient.invalidateQueries(["users", "auth-info"]);
  };

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea === localStorage && event.key === "token") {
        setIsAuthenticated(!!localStorage.getItem("token"));
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        showLoginModal,
        setShowLoginModal,
        login,
        logout,
        authInfo: authInfo || null,
        isAdminRole:
          !!authInfo?.roles?.includes("AA1") ||
          !!authInfo?.roles?.includes("AB1") ||
          !!authInfo?.roles?.includes("AC1"),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
